import React from 'react';
import { CheckCircle, Clock, XCircle, FileCheck } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import BentoWidgetCard, { WidgetQuestion } from '../BentoWidgetCard';
import { getProofsPipelineData } from '../../../../utils/aiBriefingsData';
interface ProvePipelineWidgetProps {
  onExpand?: () => void;
  onHide?: () => void;
  onAsk?: (question: string) => void;
}
const ProvePipelineWidget: React.FC<ProvePipelineWidgetProps> = ({
  onExpand,
  onHide,
  onAsk
}) => {
  const data = getProofsPipelineData();
  const questions: WidgetQuestion[] = [{
    id: '1',
    text: 'Perché aumentano i rifiuti?'
  }, {
    id: '2',
    text: 'Come ridurre i tempi di review?'
  }, {
    id: '3',
    text: 'Quali categorie hanno più problemi?'
  }, {
    id: '4',
    text: 'Confronta con periodo precedente'
  }];
  const pipelineData = [{
    label: 'Inviate',
    value: data.submitted,
    icon: FileCheck,
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  }, {
    label: 'In Revisione',
    value: data.inReview,
    icon: Clock,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50'
  }, {
    label: 'Approvate',
    value: data.approved,
    icon: CheckCircle,
    color: 'text-green-600',
    bg: 'bg-green-50'
  }, {
    label: 'Rifiutate',
    value: data.rejected,
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50'
  }];
  return <BentoWidgetCard id="prove-pipeline" title="Prove: Pipeline & Qualità" size="L" lastUpdated="3 min fa" questions={questions} onExpand={onExpand} onHide={onHide} onAsk={onAsk} data-id="element-1514">
      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-id="element-1515">
        {pipelineData.map((item, index) => <div key={index} className={`${item.bg} rounded-lg p-3`} data-id="element-1516">
            <div className="flex items-center gap-2 mb-2" data-id="element-1517">
              <item.icon className={`h-4 w-4 ${item.color}`} data-id="element-1518" />
              <p className="text-xs font-medium text-gray-700" data-id="element-1519">{item.label}</p>
            </div>
            <p className="text-xl font-bold text-gray-900" data-id="element-1520">
              {item.value.toLocaleString()}
            </p>
          </div>)}
      </div>

      {/* Avg Review Time */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4" data-id="element-1521">
        <div className="flex items-center justify-between" data-id="element-1522">
          <div data-id="element-1523">
            <p className="text-xs font-medium text-purple-900" data-id="element-1524">
              Tempo Medio di Revisione
            </p>
            <p className="text-2xl font-bold text-purple-900 mt-1" data-id="element-1525">
              {data.avgReviewTime}
            </p>
          </div>
          <Clock className="h-8 w-8 text-purple-600" data-id="element-1526" />
        </div>
      </div>

      {/* Trend Chart */}
      <div className="mb-4" data-id="element-1527">
        <p className="text-xs font-semibold text-gray-900 mb-2" data-id="element-1528">
          Trend 30 giorni
        </p>
        <div className="h-40" data-id="element-1529">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1530">
            <AreaChart data={data.trend} data-id="element-1531">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1532" />
              <XAxis dataKey="date" stroke="#6B7280" fontSize={10} tickFormatter={value => {
              const date = new Date(value);
              return `${date.getDate()}/${date.getMonth() + 1}`;
            }} data-id="element-1533" />
              <YAxis stroke="#6B7280" fontSize={10} data-id="element-1534" />
              <Tooltip contentStyle={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '0.5rem',
              fontSize: '12px'
            }} data-id="element-1535" />
              <Legend wrapperStyle={{
              fontSize: '11px'
            }} data-id="element-1536" />
              <Area type="monotone" dataKey="submitted" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="Inviate" data-id="element-1537" />
              <Area type="monotone" dataKey="approved" stackId="1" stroke="#16A34A" fill="#16A34A" fillOpacity={0.6} name="Approvate" data-id="element-1538" />
              <Area type="monotone" dataKey="rejected" stackId="1" stroke="#DC2626" fill="#DC2626" fillOpacity={0.6} name="Rifiutate" data-id="element-1539" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Rejection Reasons */}
      <div data-id="element-1540">
        <p className="text-xs font-semibold text-gray-900 mb-2" data-id="element-1541">
          Principali Motivi di Rifiuto
        </p>
        <div className="h-32" data-id="element-1542">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1543">
            <BarChart data={data.rejectionReasons} layout="vertical" data-id="element-1544">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1545" />
              <XAxis type="number" stroke="#6B7280" fontSize={10} data-id="element-1546" />
              <YAxis type="category" dataKey="reason" stroke="#6B7280" fontSize={10} width={120} data-id="element-1547" />
              <Tooltip contentStyle={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '0.5rem',
              fontSize: '12px'
            }} data-id="element-1548" />
              <Bar dataKey="count" fill="#DC2626" radius={[0, 4, 4, 0]} data-id="element-1549" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </BentoWidgetCard>;
};
export default ProvePipelineWidget;